import { useTranscriptionSync } from '@app/modules/audit/hooks/useTranscriptionSync';
import { WpWordsType } from '@app/modules/search/types';
import { DateTimeStr } from '@mi-front/core';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import WordComponent from './WordComponent';

export interface TranscriptionSyncWrapperProps {
  words: WpWordsType[];
  isCurrentRow: boolean;
  timestamp: DateTimeStr;
}

/**
 * Wraps transcription words with playback sync: highlights current word and provides hover menu for timestamp navigation.
 */
const TranscriptionSyncWrapper = memo(
  ({ words, isCurrentRow, timestamp }: TranscriptionSyncWrapperProps) => {
    const [hoveredWord, setHoveredWord] = useState<number | null>(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const hoverTimeoutRef = useRef<NodeJS.Timeout>();
    const hoveredWordRef = useRef<number | null>(null);
    const { transcriptionPlayerSync } = useTranscriptionSync();

    const handleWordHover = useCallback((e: React.MouseEvent, index: number) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ x: rect.left, y: rect.bottom });

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      hoverTimeoutRef.current = setTimeout(() => {
        hoveredWordRef.current = index;
        setHoveredWord(index);
      }, 1000);
    }, []);

    const handleWordLeave = useCallback((e: React.MouseEvent) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoveredWordRef.current = null;
      setHoveredWord(null);
    }, []);

    const syncResult = useMemo(
      () => transcriptionPlayerSync(words, isCurrentRow, timestamp),
      [words, isCurrentRow, timestamp, transcriptionPlayerSync],
    );

    return (
      <div>
        {syncResult.map((word, index) => (
          <WordComponent
            key={`word-${index}`}
            word={word.word}
            index={index}
            isHighlighted={word.isHighlighted}
            onClick={word.onClick}
            onHover={handleWordHover}
            onLeave={handleWordLeave}
            menuPosition={menuPosition}
            isHovered={hoveredWord === index || hoveredWordRef.current === index}
            start={word.start}
            end={word.end}
          />
        ))}
      </div>
    );
  },
);

TranscriptionSyncWrapper.displayName = 'TranscriptionSyncWrapper';

export default TranscriptionSyncWrapper;
